import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Cpu, ShieldCheck, Workflow } from "lucide-react";
import { Button } from "@/components/ui/button";
import HeroNetwork from "@/components/HeroNetwork";
import { COMPANIES, SERVICES, INDUSTRIES, STATS, WHY_POINTS } from "@/data/site";
import { useSEO } from "@/hooks/useSEO";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.5 } }),
};

export default function Home() {
  useSEO({
    title: "Easyworks Industries Pvt. Ltd. — Digital Transformation Solutions",
    description: "Easyworks Industries is a group of technology companies delivering websites, custom software, AI automation, cloud, ERP/CRM and digital marketing solutions across India and the UAE.",
  });
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-[#0A2540] overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 hero-grid opacity-40" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-600/10 rounded-full blur-[100px]" />

        <div className="container mx-auto max-w-7xl px-6 py-16 lg:py-24 relative grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.div initial="hidden" animate="show" variants={fadeUp}>
              <span className="inline-block px-3 py-1 border border-blue-400/30 rounded-md text-xs font-semibold tracking-wide text-blue-300 mb-6" data-testid="hero-pill">
                One Company. Multiple Solutions.
              </span>
            </motion.div>
            <motion.h1
              initial="hidden" animate="show" custom={1} variants={fadeUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight mb-6"
              style={{ fontFamily: "Outfit" }}
            >
              Building. Automating.<br />Transforming Businesses<br />Through <span className="text-[#60A5FA]">Technology.</span>
            </motion.h1>
            <motion.p initial="hidden" animate="show" custom={2} variants={fadeUp} className="text-slate-300 text-[15px] leading-relaxed max-w-xl mb-8">
              Easyworks Industries Pvt. Ltd. is a group of technology companies providing end-to-end digital transformation solutions through innovation, automation and expert project management.
            </motion.p>

            <motion.div initial="hidden" animate="show" custom={3} variants={fadeUp} className="grid sm:grid-cols-2 gap-4 mb-8 max-w-xl">
              {[
                { icon: Workflow, t: "End-to-End", s: "Digital Solutions" },
                { icon: CheckCircle2, t: "Project Management", s: "Excellence" },
                { icon: Cpu, t: "AI Powered", s: "Automation" },
                { icon: ShieldCheck, t: "Scalable & Secure", s: "Infrastructure" },
              ].map(({ icon: Icon, t, s }, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-md bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-[#60A5FA]" />
                  </div>
                  <div>
                    <div className="text-white text-[13px] font-semibold">{t}</div>
                    <div className="text-slate-400 text-[12px]">{s}</div>
                  </div>
                </div>
              ))}
            </motion.div>

            <motion.div initial="hidden" animate="show" custom={4} variants={fadeUp} className="flex flex-wrap gap-3">
              <Link to="/contact" data-testid="hero-consult-btn">
                <Button className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-12 px-6 rounded-md font-semibold shadow-lg shadow-blue-600/30">
                  Book Free Consultation
                </Button>
              </Link>
              <Link to="/companies" data-testid="hero-explore-btn">
                <Button variant="outline" className="h-12 px-6 rounded-md font-semibold bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white gap-2">
                  Explore Our Companies <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </motion.div>
          </div>

          <div className="flex justify-center">
            <HeroNetwork />
          </div>
        </div>

        {/* Companies strip overlapping hero bottom */}
        <div className="container mx-auto max-w-7xl px-6 pb-16 relative">
          <div className="bg-white rounded-xl shadow-2xl shadow-black/20 p-6 md:p-8">
            <div className="text-center mb-5">
              <span className="section-label" data-testid="companies-strip-label">Easyworks Group of Companies</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {COMPANIES.map((c) => {
                const Icon = c.icon;
                return (
                  <Link
                    key={c.key}
                    to={`/companies#${c.key}`}
                    data-testid={`hero-company-card-${c.key}`}
                    className="flex items-center gap-2.5 p-3 rounded-lg border border-slate-100 hover:border-blue-200 hover:bg-blue-50/40 transition-colors group"
                  >
                    <div className={`w-9 h-9 rounded-md bg-gradient-to-br ${c.color} flex items-center justify-center shrink-0`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[12px] font-semibold text-slate-900 truncate group-hover:text-[#2563EB] transition-colors">{c.name}</div>
                      <div className="text-[10px] text-slate-500 truncate">{c.tag}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="text-center text-[12px] text-slate-500 mt-4">All companies are part of Easyworks Industries Pvt. Ltd.</div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 lg:py-24" data-testid="services-section">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="text-center mb-14">
            <span className="section-label">Our Services</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Complete Solutions for Your Business Growth</h2>
            <p className="text-slate-500 mt-3 max-w-2xl mx-auto">From websites to enterprise software, AI to cloud – we deliver everything your business needs to scale.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.key}
                  initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
                  data-testid={`service-card-${s.key}`}
                  className="p-5 rounded-xl border border-slate-200 bg-white hover:border-blue-200 hover:shadow-lg hover:shadow-blue-900/5 hover:-translate-y-1 transition-transform transition-colors transition-shadow duration-300"
                >
                  <div className="w-10 h-10 rounded-md bg-blue-50 border border-blue-100 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[#2563EB]" />
                  </div>
                  <div className="font-semibold text-slate-900 text-[15px] mb-1.5" style={{ fontFamily: "Outfit" }}>{s.title}</div>
                  <div className="text-[13px] text-slate-500 leading-relaxed">{s.desc}</div>
                </motion.div>
              );
            })}
          </div>
          <div className="text-center mt-10">
            <Link to="/services">
              <Button className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-11 px-6 rounded-md gap-2" data-testid="view-all-services-btn">
                View All Services <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-16 border-y border-slate-100 bg-slate-50/60" data-testid="industries-section">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="text-center mb-10">
            <span className="section-label">Industries We Serve</span>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Customized solutions for every industry</h2>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-10 gap-4">
            {INDUSTRIES.map((ind, i) => {
              const Icon = ind.icon;
              return (
                <motion.div
                  key={ind.key}
                  initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
                  className="flex flex-col items-center gap-2 group"
                  data-testid={`industry-${ind.key}`}
                >
                  <div className="w-14 h-14 rounded-lg bg-white border border-slate-200 flex items-center justify-center group-hover:border-blue-300 group-hover:bg-blue-50 transition-colors">
                    <Icon className="w-6 h-6 text-[#2563EB]" strokeWidth={1.6} />
                  </div>
                  <div className="text-[12px] font-medium text-slate-700 text-center">{ind.name}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-20" data-testid="why-section">
        <div className="container mx-auto max-w-7xl px-6 grid lg:grid-cols-3 gap-10">
          <div>
            <span className="section-label">Why Choose Easyworks</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: "Outfit" }}>Your Success is<br />Our Mission</h2>
            <p className="text-slate-500 mb-6 leading-relaxed">
              We combine technology, creativity and project management expertise to deliver solutions that drive real business results.
            </p>
            <Link to="/about">
              <Button className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-11 px-5 rounded-md gap-2" data-testid="know-more-btn">
                Know More About Us <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
          <div className="lg:col-span-2">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
              {STATS.map((s, i) => (
                <div key={i} className="p-4 rounded-lg bg-white border border-slate-200 text-center" data-testid={`stat-${i}`}>
                  <div className="text-2xl font-extrabold text-[#2563EB]" style={{ fontFamily: "Outfit" }}>{s.value}</div>
                  <div className="text-[11px] text-slate-500 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {WHY_POINTS.map((w, i) => (
                <div key={i} className="flex gap-3 p-4 rounded-lg bg-slate-50/60 border border-slate-100">
                  <CheckCircle2 className="w-4 h-4 text-[#2563EB] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-[14px] font-semibold text-slate-900">{w.title}</div>
                    <div className="text-[12.5px] text-slate-500 leading-relaxed">{w.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* THE FAMILY */}
      <section className="py-20 bg-slate-50/60 border-t border-slate-100" data-testid="family-section">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="text-center mb-14">
            <span className="section-label">Our Companies</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>The Easyworks Family</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {COMPANIES.map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.div
                  key={c.key}
                  initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
                  data-testid={`family-card-${c.key}`}
                  className="p-6 rounded-xl bg-white border border-slate-200 hover:border-blue-200 hover:shadow-lg hover:shadow-blue-900/5 hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-11 h-11 rounded-lg bg-gradient-to-br ${c.color} flex items-center justify-center`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>{c.name}</div>
                      <div className="text-[11px] text-slate-500">{c.tag}</div>
                    </div>
                  </div>
                  <p className="text-[13px] text-slate-500 leading-relaxed mb-4">{c.description}</p>
                  <Link to={`/companies#${c.key}`} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#2563EB] hover:text-[#1D4ED8] transition-colors">
                    Learn More <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
